import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

interface LocalStorageListenersType {
  [key: string]: Array<Dispatch<SetStateAction<string>>>
}

const localStorageListeners: LocalStorageListenersType = {}

function readStringFromLocalStorage<T extends string | null>(
  key: string,
  defaultState: T,
): T {
  return typeof window !== 'undefined'
    ? ((localStorage.getItem(key) as T) ?? defaultState)
    : defaultState
}

function useLocalStorageStringState<T extends string | null>(
  key: string,
  defaultState: T,
): [T, (newState: T) => void] {
  const state = readStringFromLocalStorage(key, defaultState)

  const [, notify] = useState(key + '\n' + state)

  useEffect(() => {
    if (!localStorageListeners[key]) {
      localStorageListeners[key] = []
    }
    localStorageListeners[key].push(notify)
    return () => {
      localStorageListeners[key] = localStorageListeners[key].filter(
        (listener) => listener !== notify,
      )
      if (localStorageListeners[key].length === 0) {
        delete localStorageListeners[key]
      }
    }
  }, [key])

  const setState = useCallback<(newState: T) => void>(
    (newState) => {
      if (!localStorageListeners[key]) {
        localStorageListeners[key] = []
      }

      if (newState === null) {
        localStorage.removeItem(key)
      } else {
        localStorage.setItem(key, newState)
      }
      localStorageListeners[key].forEach((listener) =>
        listener(key + '\n' + newState),
      )
    },
    [key],
  )

  return [state, setState]
}

declare const localStorageKeySymbol: unique symbol

export interface LocalStorageKey<T> {
  readonly key: string
  readonly defaultValue: T
  // prevent creating instances outside of this module
  [localStorageKeySymbol]: typeof localStorageKeySymbol
}

export function localStorageKey<T = unknown>(
  key: string,
): LocalStorageKey<Exclude<T, undefined> | null>
export function localStorageKey<T>(
  key: string,
  defaultValue: Exclude<T, undefined>,
): LocalStorageKey<Exclude<T, undefined>>
export function localStorageKey<T>(
  key: string,
  defaultValue?: T,
): LocalStorageKey<T> {
  return { key, defaultValue } as LocalStorageKey<T>
}

export function readFromLocalStorage<T>({
  key,
  defaultValue,
}: LocalStorageKey<T>): T {
  const stringState = readStringFromLocalStorage(
    key,
    JSON.stringify(defaultValue),
  )
  if (!stringState) {
    return defaultValue
  }

  try {
    return JSON.parse(stringState) as T
  } catch (e) {
    console.log(`Error parsing JSON from localStorage: key=${key}`, e)
    return defaultValue
  }
}

export function useLocalStorage<T>({
  key,
  defaultValue,
}: LocalStorageKey<T>): [T, (newState: T) => void] {
  const [stringState, setStringState] = useLocalStorageStringState(
    key,
    defaultValue === null ? null : JSON.stringify(defaultValue),
  )

  const setState = useCallback(
    (newState: T) => {
      setStringState(newState === null ? null : JSON.stringify(newState))
    },
    [setStringState],
  )

  const state = useMemo(() => {
    try {
      if (stringState == null) {
        return defaultValue
      }
      return JSON.parse(stringState) as T
    } catch (e) {
      console.log(`Error parsing JSON from localStorage: key=${key}`, e)
      return defaultValue
    }
  }, [key, stringState, defaultValue])

  return [state, setState]
}
